import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Paper,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { helpWhy } from '../../help/why';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { ApiKeyView, INTEGRATION_SCOPES, IntegrationKeyService } from '../../services/integration.service';

/** Integration API keys: the raw key is shown once at creation and never again; only a hash is stored. */
const ApiKeysPanel: React.FC = () => {
    const toast = useToast();
    const [keys, setKeys] = useState<ApiKeyView[]>([]);
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [scopes, setScopes] = useState<string[]>([]);
    const [expires, setExpires] = useState('');
    const [revealed, setRevealed] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            setKeys(await IntegrationKeyService.list());
        } catch (e) {
            toast.showApiError(e, 'Failed to load API keys');
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const toggle = (s: string) => setScopes(cur => (cur.includes(s) ? cur.filter(x => x !== s) : [...cur, s]));

    const create = async () => {
        if (busy) return;
        setBusy(true);
        try {
            const res = await IntegrationKeyService.create(name.trim(), scopes, expires || undefined);
            setOpen(false);
            setName(''); setScopes([]); setExpires('');
            setRevealed(res.rawKey);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Could not create the key');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async (k: ApiKeyView) => {
        if (!window.confirm(`Revoke "${k.name}"? Anything using it stops working immediately.`)) return;
        try {
            await IntegrationKeyService.revoke(k.id);
            toast.showSuccess('Key revoked');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Could not revoke the key');
        }
    };

    return (
        <Box data-help-page="data.apiKeys.page">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Read-only keys for another system. Send the key in the <code>X-API-Key</code> header to <code>/api/integration/v1/*</code>.
                A key is limited to this institution and the scopes you tick, and can expire or be revoked.
            </Typography>
            <Button data-help="data.apiKeys.action.create" variant="contained" sx={{ mb: 2 }} onClick={() => setOpen(true)}>Create API key</Button>
            {revealed && (
                <Alert severity="warning" sx={{ mb: 2 }} data-testid="revealed-key" onClose={() => setRevealed(null)}>
                    Copy this key now; it is shown only once: <code>{revealed}</code>
                </Alert>
            )}
            <TableContainer component={Paper}>
                <Table size="small" data-help-section="data.apiKeys.table">
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="data.apiKeys.col.name">Name</TableCell><TableCell data-help="data.apiKeys.col.key">Key</TableCell><TableCell data-help="data.apiKeys.col.scopes">Scopes</TableCell><TableCell data-help="data.apiKeys.col.expires">Last day it works</TableCell>
                            <TableCell data-help="data.apiKeys.col.lastUsed">Last used</TableCell><TableCell data-help="data.apiKeys.col.status">Status</TableCell><TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.length === 0 && <TableRow><TableCell colSpan={7}>No API keys yet.</TableCell></TableRow>}
                        {keys.map(k => (
                            <TableRow key={k.id}>
                                <TableCell>{k.name}</TableCell>
                                <TableCell><code>{k.keyPrefix}...</code></TableCell>
                                <TableCell>{k.scopes.join(', ')}</TableCell>
                                <TableCell>{k.expiresOn ? formatDate(k.expiresOn) : 'Never'}</TableCell>
                                <TableCell>{k.lastUsedAt ? formatDateTime(k.lastUsedAt) : 'Never'}</TableCell>
                                <TableCell><Chip size="small" label={k.revoked ? 'Revoked' : 'Active'} color={k.revoked ? 'default' : 'success'} /></TableCell>
                                <TableCell align="right">
                                    {!k.revoked && <Button data-help="data.apiKeys.action.revoke" size="small" color="error" onClick={() => revoke(k)}>Revoke</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" data-help-page="data.apiKeys.create.page">
                <DialogTitle>Create API key</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField label="Name" size="small" data-help="data.apiKeys.create.field.name" value={name} onChange={e => setName(e.target.value)} fullWidth inputProps={{ maxLength: 100 }} sx={{ mt: 1 }} />
                    <TextField label="Last day it works (Dhaka, optional)" type="date" size="small" data-help="data.apiKeys.create.field.expires" value={expires} InputLabelProps={{ shrink: true }}
                        inputProps={{ min: new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dhaka' }).format(new Date()) }}
                        onChange={e => setExpires(e.target.value)} fullWidth />
                    <FormGroup>
                        {INTEGRATION_SCOPES.map(s => (
                            <FormControlLabel key={s} data-help="data.apiKeys.create.field.scope" control={<Checkbox checked={scopes.includes(s)} onChange={() => toggle(s)} />} label={s} />
                        ))}
                    </FormGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} data-help="data.apiKeys.create.cancel">Cancel</Button>
                    <Button variant="contained" disabled={!name.trim() || scopes.length === 0 || busy} onClick={create} data-help="data.apiKeys.action.confirmCreate" {...helpWhy(!busy && (!name.trim() || scopes.length === 0) ? { kind: 'status', message: 'Type a name and tick at least one scope.' } : null)}>Create</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ApiKeysPanel;
